/*
 * nadi-coupon.js — make the coupon field collect, APPLY, and report.
 *
 * WHAT WAS WRONG. Measured on production 2026-08-24. The field exists in two
 * places — the cart drawer behind "הוסף קופון", and the cart page as
 * #CartDiscountcode — and was wired to nothing at all:
 *
 *   - it carries data-name="discount" and NO name attribute, so it is never
 *     serialised with its form
 *   - "discount" appears ZERO times in global.min.js (224KB), theme.min.js
 *     (135KB) and custom.js; only analytics pixels mention it
 *   - new FormData(form) yields exactly three fields: updates[], note and
 *     attributes[collection_items_per_row]
 *   - typed NADITEST99, clicked checkout, intercepted the submit: the code
 *     reached nothing
 *
 * The store has four ACTIVE codes — OLIO50, SASSONIC, OLIO40, SOS10 — and
 * every one of them was being discarded.
 *
 * ── WHY THE FIRST VERSION WAS NOT ENOUGH ──────────────────────────────────
 * It routed the code through the checkout button only. The code worked, but
 * there was no way to APPLY it and see the result while still in the cart, and
 * no button to press — Nadav typed a code, found nothing to click, and was
 * right to call it broken. A coupon field with no apply control is still a
 * field that does nothing, whatever happens later.
 *
 * So this version adds the control, and the answer:
 *
 *   apply  ->  /discount/<CODE>?redirect=/cart  ->  cart reloads
 *          ->  read cart.js and say whether it actually landed
 *
 * /discount/<CODE> is Shopify's own documented discount link. It applies the
 * code to the session, needs no app, and the cart template already renders the
 * result — an existing automatic discount shows there today.
 *
 * ── WHY IT REPORTS BACK ───────────────────────────────────────────────────
 * A code can be valid and still not apply: SOS10 is 10% on two specific
 * products and does not combine with shipping discounts. Silence after
 * pressing apply is indistinguishable from the bug this file exists to fix, so
 * the attempted code is remembered across the redirect and checked against
 * cart.items[].discounts on the way back. Named there, it succeeded; absent,
 * the customer is told plainly rather than left guessing.
 *
 * ── THE CHECKOUT BUTTON IS STILL INTERCEPTED ──────────────────────────────
 * Someone will type a code and press checkout without applying it. That path
 * is kept, and only for that case: with the field empty the listener returns
 * and the theme's own submit runs exactly as it does today.
 */
(function () {
  'use strict';

  var FIELD   = '#CartDiscountcode, [data-name="discount"]';
  var PENDING = 'nadi-coupon-pending';
  var SCOPES  = '#t4s-mini_cart, .t4s-drawer__cart, [id*="mini_cart"], .t4s-cartPage__form';
  var MOUNTED = 'data-nadi-coupon';

  function field(scope) {
    return (scope || document).querySelector(FIELD) || document.querySelector(FIELD);
  }
  function noteEl(scope) {
    return (scope || document).querySelector('#CartSpecialInstructions, textarea[name="note"]')
        || document.querySelector('#CartSpecialInstructions, textarea[name="note"]');
  }

  /* Persist the note before leaving the page, or applying a code silently
     throws away whatever the customer typed into it. */
  function saveNoteThen(scope, done) {
    var el = noteEl(scope);
    if (!el) return done();
    fetch('/cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: el.value })
    }).then(done, done);
  }

  function discountUrl(code, target) {
    return '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(target);
  }

  function apply(code, scope) {
    try { sessionStorage.setItem(PENDING, code); } catch (e) {}
    saveNoteThen(scope, function () {
      location.href = discountUrl(code, '/cart');
    });
  }

  /* ── the apply button, which is the thing that was missing ───────────── */

  function mountButton(input) {
    /* hasAttribute, NOT getAttribute. setAttribute(name, '') stores an empty
       string, and getAttribute then returns '' — which is falsy, so the guard
       never fired. Every observer pass re-mounted the button, and mounting is
       itself a childList mutation, which fired the observer again: an infinite
       loop that froze the cart page. It hung my own browser twice before Nadav
       reported it, and I wrote those hangs off as a flaky tool. */
    if (!input || input.hasAttribute(MOUNTED)) return;
    input.setAttribute(MOUNTED, '1');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nadi-coupon__apply';
    btn.textContent = 'החל';
    btn.addEventListener('click', function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      var code = (input.value || '').trim();
      if (!code) { input.focus(); return; }
      btn.disabled = true;
      btn.textContent = 'מחיל…';
      apply(code, input.closest(SCOPES));
    });

    /* Enter inside the field means apply, not "submit the cart form" — which
       on this theme would reload the cart and drop the code. */
    input.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Enter') return;
      ev.preventDefault();
      btn.click();
    });

    var wrap = document.createElement('div');
    wrap.className = 'nadi-coupon';
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);
    wrap.appendChild(btn);
  }

  /* ── say what happened, once we are back ─────────────────────────────── */

  function show(ok, code) {
    var input = field();
    var host = input ? (input.closest('.nadi-coupon') || input.parentNode) : null;
    if (!host || !host.parentNode) return;
    var old = document.querySelector('.nadi-coupon__msg');
    if (old) old.remove();
    var msg = document.createElement('p');
    msg.className = 'nadi-coupon__msg' + (ok ? ' is-ok' : ' is-bad');
    msg.setAttribute('role', 'status');
    msg.textContent = ok
      ? 'הקופון ' + code + ' הופעל'
      : 'הקוד ' + code + ' לא חל על הפריטים שבעגלה';
    host.parentNode.insertBefore(msg, host.nextSibling);
    if (ok && input) input.value = code;
  }

  function report() {
    var code = null;
    try { code = sessionStorage.getItem(PENDING); } catch (e) { return; }
    if (!code) return;
    try { sessionStorage.removeItem(PENDING); } catch (e) {}

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var names = [];
        (cart.items || []).forEach(function (it) {
          (it.discounts || []).forEach(function (d) { names.push(String(d.title).toUpperCase()); });
        });
        (cart.cart_level_discount_applications || []).forEach(function (d) {
          names.push(String(d.title).toUpperCase());
        });
        show(names.indexOf(code.toUpperCase()) > -1, code);
      })
      .catch(function () {});
  }

  /* ── the safety net: typed a code, went straight to checkout ─────────── */

  function isCheckoutControl(el) {
    if (!el) return false;
    if (el.name === 'checkout') return true;
    var href = el.getAttribute && el.getAttribute('href');
    if (href && /\/checkout(\?|$)/.test(href)) return true;
    var t = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return /^להשלמת ה?הזמנה$/.test(t);
  }

  document.addEventListener('click', function (ev) {
    var node = ev.target, ctl = null;
    for (var i = 0; i < 5 && node && !ctl; i++, node = node.parentElement) {
      if (isCheckoutControl(node)) ctl = node;
    }
    if (!ctl) return;
    var scope = ctl.closest(SCOPES) || document;
    var input = field(scope);
    var code = input && input.value ? input.value.trim() : '';
    if (!code) return;                       /* untouched: the theme's own path */
    ev.preventDefault();
    ev.stopImmediatePropagation();
    try { sessionStorage.removeItem(PENDING); } catch (e) {}
    saveNoteThen(scope, function () {
      location.href = discountUrl(code, '/checkout');
    });
  }, true);

  /* ── boot, and keep booting: the drawer is built lazily ──────────────── */

  function scan() {
    document.querySelectorAll(FIELD).forEach(mountButton);
  }

  function start() {
    scan();
    report();
    /* Belt and braces: the observer is disconnected while scan() mutates, so
       even a future guard slip cannot turn one insertion into a loop. */
    var mo = new MutationObserver(function () {
      mo.disconnect();
      try { scan(); } finally {
        mo.observe(document.body, { childList: true, subtree: true });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
